import { useCallback, useEffect, useState } from 'react';
import { ERROR_MESSAGE } from 'appConstants';
import { PricingSummaryDetailsApi } from 'components/pricingSummaryDetails/pricingSummaryDetailsApi';
import { buildSelectionOrders, pickBasketOrder } from 'subscriptionOptions/pricingRequest';
import { GridSelection, PackageResult } from 'subscriptionOptions/types';
import { PromoCodeBySubject } from 'subscriptionOptions/renewalPromoCodes';
import {
  buildRenewalPriceInfo,
  buildRenewalSelectedPackages,
  RenewalPriceInfo,
  RenewalSelectedPackage,
} from 'subscriptionOptions/renewalPricing';

type RenewalPricingResult = {
  priceInfo: RenewalPriceInfo | null;
  selectedPackages: RenewalSelectedPackage[];
  /** The priced packages, for working out what each applied code saved. */
  pricedPackages: PackageResult[];
  loading: boolean;
  error: string;
  /** Prices the same basket again, for when the server says the price has changed. */
  reload: () => void;
};

/** Prices a renewal basket for the payment forms; the panel chose the packages. */
export const useFetchRenewalPricing = (
  selection: GridSelection,
  promoCodeBySubject: PromoCodeBySubject
): RenewalPricingResult => {
  const [priceInfo, setPriceInfo] = useState<RenewalPriceInfo | null>(null);
  const [selectedPackages, setSelectedPackages] = useState<RenewalSelectedPackage[]>([]);
  const [pricedPackages, setPricedPackages] = useState<PackageResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [reloadCount, setReloadCount] = useState<number>(0);

  // Serialised so a new object describing the same basket does not refetch.
  const basketKey = JSON.stringify([selection, promoCodeBySubject]);

  /** Returns an error message, or '' when the basket priced successfully. */
  const price = useCallback(
    async (): Promise<string> => {
      const [ownSelection, ownCodes]: [GridSelection, PromoCodeBySubject] = JSON.parse(basketKey);

      if (Object.keys(ownSelection).length === 0) {
        setPriceInfo(null);
        setSelectedPackages([]);
        setPricedPackages([]);
        return '';
      }

      const result = await PricingSummaryDetailsApi.getSchoolPricingSummaryDetails(
        buildSelectionOrders(ownSelection, ownCodes)
      );

      if (!result.data) {
        // Rejected, commonly an invalid code. The last accepted price stays on screen.
        return (result.error && result.error.message) || ERROR_MESSAGE;
      }

      const picked = pickBasketOrder(result.data);

      if ('error' in picked) {
        return picked.error;
      }

      setPriceInfo(buildRenewalPriceInfo(picked.order));
      setSelectedPackages(buildRenewalSelectedPackages(picked.order));
      setPricedPackages((picked.order.orderDiscountResult || ({} as any)).packageResults || []);
      return '';
    },
    [basketKey]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const message = await price();
      if (cancelled) return;
      setError(message);
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [price, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { priceInfo, selectedPackages, pricedPackages, loading, error, reload };
};
